import { useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

// Deduplication: track sent error messages with timestamps
const recentErrors = new Map<string, number>();
const DEDUP_INTERVAL_MS = 60_000; // 60 seconds

// Session ID for rate-limit tracking on the backend
const sessionId = crypto.randomUUID();

// Patterns to ignore
const IGNORED_PATTERNS = [
  "ResizeObserver",
  "chrome-extension://",
  "moz-extension://",
  "extensions/",
  "Loading chunk",
  "Loading CSS chunk",
  "dynamically imported module",
];

function shouldIgnore(message: string): boolean {
  return IGNORED_PATTERNS.some((p) => message.includes(p));
}

function getDeviceType(): string {
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

/**
 * Send an error report to the error-report edge function.
 * Fire-and-forget — errors in reporting are swallowed.
 */
function reportToDiscord(payload: {
  type: string;
  message: string;
  stack?: string;
  componentStack?: string;
}) {
  // Dedup check
  const dedupKey = `${payload.type}:${payload.message}`;
  const now = Date.now();
  const lastSent = recentErrors.get(dedupKey);
  if (lastSent && now - lastSent < DEDUP_INTERVAL_MS) {
    return; // Already sent recently
  }
  recentErrors.set(dedupKey, now);

  // Cleanup old entries
  if (recentErrors.size > 50) {
    for (const [key, ts] of recentErrors) {
      if (now - ts > DEDUP_INTERVAL_MS * 2) recentErrors.delete(key);
    }
  }

  const body = {
    source: "frontend",
    type: payload.type,
    message: payload.message,
    stack: payload.stack || undefined,
    componentStack: payload.componentStack || undefined,
    route: window.location.pathname,
    userAgent: navigator.userAgent,
    deviceType: getDeviceType(),
    sessionId,
    timestamp: new Date().toISOString(),
  };

  // Fire-and-forget
  supabase.functions
    .invoke("error-report", { body })
    .catch(() => {
      // Silently fail — we don't want error reporting to cause more errors
    });
}

/**
 * Global error handler for unhandled promise rejections and errors.
 * This prevents the entire app from crashing when async errors occur
 * outside of React's error boundaries.
 * 
 * Also sends error reports to Discord via the error-report edge function.
 */
export const useGlobalErrorHandler = () => {
  useEffect(() => {
    // Handle unhandled promise rejections (async errors)
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled promise rejection:", event.reason);
      
      // Prevent the default browser behavior (which might crash the page)
      event.preventDefault();
      
      // Show user-friendly error message
      const message = event.reason?.message || "Ein unerwarteter Fehler ist aufgetreten";
      
      // Check if we should ignore this error
      if (shouldIgnore(message)) return;

      // Only show toast if it's not a network error (those are usually handled elsewhere)
      if (!message.includes("fetch") && !message.includes("network")) {
        toast.error("Fehler", {
          description: message,
        });
      }

      // Report to Discord
      reportToDiscord({
        type: "unhandledrejection",
        message,
        stack: event.reason?.stack,
      });
    };

    // Handle uncaught errors
    const handleError = (event: ErrorEvent) => {
      console.error("Uncaught error:", event.error);
      
      // Prevent the default browser behavior
      event.preventDefault();
      
      const message = event.message || "Unknown error";

      // Check if we should ignore this error
      if (shouldIgnore(message)) return;
      
      toast.error("Fehler", {
        description: "Ein Fehler ist aufgetreten. Bitte lade die Seite neu.",
      });

      // Report to Discord
      reportToDiscord({
        type: "error",
        message,
        stack: event.error?.stack,
      });
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleError);

    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      window.removeEventListener("error", handleError);
    };
  }, []);
};

// Export for use in ErrorBoundary (class component can't use hooks)
export { reportToDiscord };
